import { useState } from "react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

interface CreateTaxRateDialogProps {
  organisationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export function CreateTaxRateDialog({
  organisationId,
  open,
  onOpenChange,
  onCreated,
}: CreateTaxRateDialogProps) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function resetForm() {
    setCode("");
    setName("");
    setRate("");
    setSubmitting(false);
    setError(null);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError("Code is required.");
      return;
    }
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    const rateNum = Number(rate);
    if (isNaN(rateNum) || rateNum < 0 || rateNum > 100) {
      setError("Rate must be between 0 and 100.");
      return;
    }

    setSubmitting(true);
    const { error: apiError } = await api.POST("/v1/tax-rates", {
      body: { code: code.trim(), name: name.trim(), rate: rateNum },
      headers: { "x-dolfin-organisation-id": organisationId },
    });

    if (apiError) {
      setError("Failed to create tax rate.");
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    onCreated();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Tax Rate</DialogTitle>
            <DialogDescription>
              Add a new tax rate for this organisation.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tax-rate-code">Code</Label>
              <Input
                id="tax-rate-code"
                placeholder="e.g. VAT20"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax-rate-name">Name</Label>
              <Input
                id="tax-rate-name"
                placeholder="e.g. Standard VAT"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tax-rate-rate">Rate (%)</Label>
              <Input
                id="tax-rate-rate"
                type="number"
                min={0}
                max={100}
                step="0.01"
                placeholder="e.g. 20"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
              />
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
